import React from "react";
import { IDENTITY_QUALIFIER } from "./gradeMath";

const Range = ({ label, lo, hi, min, max, step, onLo, onHi, testPrefix }) => (
  <div className="space-y-0.5">
    <div className="flex justify-between text-[9px] font-mono text-white/40">
      <span>{label}</span><span>{Number(lo).toFixed(label === "HUE" ? 0 : 2)} – {Number(hi).toFixed(label === "HUE" ? 0 : 2)}</span>
    </div>
    <div className="flex gap-1">
      <input type="range" min={min} max={max} step={step} value={lo}
        onChange={(e) => onLo(parseFloat(e.target.value))}
        className="flex-1 accent-orange-500" data-testid={`${testPrefix}-lo`} />
      <input type="range" min={min} max={max} step={step} value={hi}
        onChange={(e) => onHi(parseFloat(e.target.value))}
        className="flex-1 accent-orange-500" data-testid={`${testPrefix}-hi`} />
    </div>
  </div>
);

/**
 * HSL Qualifier — select a colour range, grade only those pixels.
 * Preview-accurate in both engines; SERVER RENDER IS BLOCKED until the P3
 * worker ships qualifier support (409 UNSUPPORTED_OPS by design).
 */
export const QualifierControls = ({ qualifier, onChange, matteOn, onMatteToggle }) => {
  const q = { ...IDENTITY_QUALIFIER(), ...(qualifier || {}) };
  const set = (patch) => onChange({ ...q, ...patch });

  return (
    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 space-y-2"
      data-testid="grade-qualifier-controls">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] font-mono uppercase tracking-widest text-orange-300/80">HSL Qualifier</h4>
        <div className="flex items-center gap-2">
          {q.enabled && (
            <button onClick={onMatteToggle}
              data-testid="qualifier-matte-btn"
              className={`text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                matteOn ? "border-orange-500 text-orange-300" : "border-white/15 text-white/40 hover:text-white"}`}>
              MATTE
            </button>
          )}
          <button onClick={() => set({ enabled: !q.enabled })}
            data-testid="qualifier-enable-btn"
            className={`text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
              q.enabled ? "border-emerald-500 text-emerald-300" : "border-white/15 text-white/40 hover:text-white"}`}>
            {q.enabled ? "ON" : "OFF"}
          </button>
        </div>
      </div>
      {q.enabled && (
        <>
          <Range label="HUE" lo={q.hue_min} hi={q.hue_max} min={0} max={360} step={1}
            onLo={(v) => set({ hue_min: v })} onHi={(v) => set({ hue_max: v })} testPrefix="qual-hue" />
          <Range label="SAT" lo={q.sat_min} hi={q.sat_max} min={0} max={1} step={0.01}
            onLo={(v) => set({ sat_min: v })} onHi={(v) => set({ sat_max: v })} testPrefix="qual-sat" />
          <Range label="LUM" lo={q.lum_min} hi={q.lum_max} min={0} max={1} step={0.01}
            onLo={(v) => set({ lum_min: v })} onHi={(v) => set({ lum_max: v })} testPrefix="qual-lum" />
          <div className="flex items-center gap-2 text-[9px] font-mono text-white/40">
            <span>SOFT</span>
            <input type="range" min={0} max={1} step={0.01} value={q.softness}
              onChange={(e) => set({ softness: parseFloat(e.target.value) })}
              className="flex-1 accent-orange-500" data-testid="qual-softness" />
            <span>{q.softness.toFixed(2)}</span>
          </div>
          <p className="text-[9px] text-amber-400/60">
            Preview-only until the render worker ships qualifier support — render
            will be blocked while this node is enabled.
          </p>
        </>
      )}
    </div>
  );
};
